import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { connect, createLocalTracks } from "twilio-video";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { PUBLIC_BASE } from "../lib/publicBase";
import { useAuth } from "../context/AuthContext";
import { Radio, Eye } from "lucide-react";
import { toast } from "sonner";
import LiveNowPanel from "../components/LiveNowPanel";

export default function GoLive() {
  const { uid: viewerOfHostId } = useParams(); // /live/:uid for viewers
  const { user } = useAuth();
  const isViewer = !!viewerOfHostId;
  const hostId = isViewer ? viewerOfHostId : user?.id;
  const roomName = hostId ? `live_${hostId}` : null;
  const nav = useNavigate();

  const [title, setTitle] = useState("");
  const [room, setRoom] = useState(null);
  const [localTracks, setLocalTracks] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [hostName, setHostName] = useState("");
  const videoBoxRef = useRef(null);
  const heartbeatRef = useRef(null);

  const start = async () => {
    try {
      const { data: tk } = await api.post("/video/token", {
        roomName,
        role: isViewer ? "viewer" : "host",
      });
      let tracks = [];
      if (!isViewer) {
        tracks = await createLocalTracks({ audio: true, video: { width: 1280 } });
        setLocalTracks(tracks);
        if (videoBoxRef.current) {
          const vt = tracks.find((t) => t.kind === "video");
          if (vt) videoBoxRef.current.appendChild(vt.attach());
        }
        // Register the broadcast on the backend BEFORE the LIVE post so
        // seekers who tap the notification land on a discoverable room.
        try {
          await api.post("/live/start", { title: title || null });
          // Kick off a 30-second heartbeat so the /api/live/active list
          // reflects reality even if the tab is minimised. Cleared in stop().
          heartbeatRef.current = setInterval(() => {
            api.post("/live/heartbeat").catch(() => { /* transient */ });
          }, 30_000);
        } catch (e) {
          // Failing to register isn't fatal — the Twilio room still works,
          // just no Live Now discovery + no broadcast notification.
          console.warn("live/start failed:", e);
        }
        await api.post("/posts", {
          content: title || "🔴 Live now",
          type: "LIVE",
          linkUrl: `${window.location.origin}/live/${user.id}`,
        });
      }
      const r = await connect(tk.token, {
        name: roomName,
        tracks,
        audio: !isViewer,
        video: !isViewer,
      });
      setRoom(r);
      setViewerCount(r.participants.size);

      const onSubscribe = (track) => {
        if (videoBoxRef.current) {
          const el = track.attach();
          if (track.kind === "video") {
            el.style.width = "100%";
            el.style.height = "100%";
            el.style.objectFit = "cover";
          }
          videoBoxRef.current.appendChild(el);
        }
      };
      r.participants.forEach((p) => {
        p.tracks.forEach((pub) => pub.isSubscribed && pub.track && onSubscribe(pub.track));
        p.on("trackSubscribed", onSubscribe);
      });
      r.on("participantConnected", (p) => {
        setViewerCount((v) => v + 1);
        p.on("trackSubscribed", onSubscribe);
      });
      r.on("participantDisconnected", () => setViewerCount((v) => Math.max(0, v - 1)));
      toast.success(isViewer ? "Joined the broadcast" : "You are LIVE — seekers are being notified");
    } catch (e) {
      console.error(e);
      // Twilio room-join 401 happens when the access token can't create a room
      // (typically because client-initiated room creation is disabled on the account).
      const code = e?.code || e?.response?.data?.detail;
      const msg = String(e?.message || code || "");
      if (msg.includes("20101") || msg.toLowerCase().includes("access token") || msg.toLowerCase().includes("unauthorized") || msg.includes("401")) {
        toast.error("Live broadcast service isn't activated yet. Admin: enable client-initiated room creation in Twilio Console → Programmable Video → Manage → Room Settings.");
      } else {
        toast.error(e.message || "Could not start broadcast");
      }
    }
  };

  const stop = () => {
    localTracks.forEach((t) => t.stop());
    if (room) room.disconnect();
    setRoom(null);
    setLocalTracks([]);
    if (videoBoxRef.current) videoBoxRef.current.innerHTML = "";
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (!isViewer) {
      // Fire-and-forget — server auto-expires after 90s of no heartbeat
      // even if this request fails, so it's safe to swallow errors.
      api.post("/live/stop").catch(() => {});
    }
    toast.success(isViewer ? "Left broadcast" : "Live ended");
    if (isViewer) nav(-1);
  };

  useEffect(() => {
    if (isViewer) {
      api.get(`/providers/${viewerOfHostId}`).then(({ data }) => setHostName(data.businessName)).catch(() => {});
      start();
    }
    return () => {
      localTracks.forEach((t) => t.stop());
      if (room) room.disconnect();
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
    // eslint-disable-next-line
  }, [viewerOfHostId]);

  if (isViewer) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="border-b border-secondary/15 p-4 flex justify-between items-center">
          <div>
            <div className="label-mystic">Live Broadcast</div>
            <div className="font-serif text-2xl">{hostName}</div>
          </div>
          <span className="text-xs px-3 py-1 bg-primary/20 text-primary border border-primary/40 uppercase tracking-wider pulse-ember">● LIVE</span>
        </div>
        <div className="flex-1 p-6">
          <div ref={videoBoxRef} className="aspect-video w-full max-w-5xl mx-auto bg-black border border-secondary/15 [&>video]:w-full [&>video]:h-full [&>video]:object-cover" data-testid="viewer-stream" />
        </div>
        <div className="p-6 flex justify-center">
          <button onClick={stop} className="bg-card border border-secondary/30 px-8 py-3 rounded-sm uppercase tracking-wider text-xs" data-testid="viewer-leave-btn">Leave Broadcast</button>
        </div>
      </div>
    );
  }

  const isProvider = user?.role === "PROVIDER" || user?.role === "ADMIN";

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <div className="label-mystic">Broadcast</div>
          <h1 className="font-serif text-5xl mt-2">
            {isProvider ? "Go Live Studio" : "Live Broadcasts"}
          </h1>
          {!isProvider && (
            <p className="text-foreground/60 text-sm mt-2">
              Practitioners broadcast rituals, teachings, and Q&amp;A sessions here. Tap any card
              below to join a live session in progress.
            </p>
          )}
        </div>

        <LiveNowPanel heading={isProvider ? "Who is live right now" : "Live Now"} />

        {isProvider && (
        <div className="parchment p-6">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What is being summoned tonight?"
            className="w-full bg-input border border-secondary/20 p-3 rounded-sm mb-4" data-testid="golive-title" />
          <div className="aspect-video bg-black border border-secondary/15 relative overflow-hidden" data-testid="golive-preview">
            <div ref={videoBoxRef} className="w-full h-full [&>video]:w-full [&>video]:h-full [&>video]:object-cover" />
            {!room && <div className="absolute inset-0 flex items-center justify-center text-foreground/40 font-serif text-2xl">Preview will appear here</div>}
            {room && (
              <>
                <span className="absolute top-4 left-4 px-3 py-1 bg-primary text-primary-foreground text-xs uppercase tracking-wider pulse-ember" data-testid="golive-indicator">● LIVE</span>
                <span className="absolute top-4 right-4 px-3 py-1 bg-black/60 text-secondary text-xs uppercase tracking-wider flex items-center gap-1" data-testid="golive-viewers">
                  <Eye size={12} strokeWidth={1.25} /> {viewerCount} watching
                </span>
              </>
            )}
          </div>
          <div className="mt-4 flex gap-3">
            {!room ? (
              <button onClick={start} className="bg-primary text-primary-foreground px-8 py-3 rounded-sm uppercase tracking-[0.2em] text-xs flex items-center gap-2" data-testid="golive-start-btn">
                <Radio size={14} strokeWidth={1.25} /> Start Broadcasting
              </button>
            ) : (
              <button onClick={stop} className="bg-destructive text-destructive-foreground px-8 py-3 rounded-sm uppercase tracking-[0.2em] text-xs" data-testid="golive-stop-btn">End Live</button>
            )}
          </div>
          {room && (
            <div className="mt-4 parchment p-3 text-xs text-foreground/70" data-testid="golive-share-link">
              Share link: <code className="text-secondary">{`${PUBLIC_BASE}/live/${user.id}`}</code>
            </div>
          )}
        </div>
        )}
      </div>
    </Layout>
  );
}
